import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// VIDEO SERVICE — TB awareness video library (official Central TB Division / Ni-kshay).
// Public GET /videos (filtered) + /videos/{slug} + /videos/facets; admin CRUD.
// Videos link to official sources (YouTube embed via youtubeId, or externalUrl for
// Google Drive / other). No government content or video IDs are fabricated.
// ================================================================================

/** The 8 browse categories of the video library. */
export type VideoCategory =
  | 'TB_AWARENESS' | 'SYMPTOMS' | 'TREATMENT' | 'NUTRITION' | 'STIGMA'
  | 'SURVIVOR_STORIES' | 'NIKSHAY_MITRA' | 'COMMUNITY_CAMPAIGNS'

export type ResourceStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export interface Video {
  id: number
  title: string
  slug: string
  description?: string
  category: VideoCategory
  youtubeId?: string | null
  externalUrl?: string | null
  thumbnailUrl?: string | null
  duration?: string | null
  language?: string | null
  sourceOrg?: string | null
  isFeatured?: boolean
  status: ResourceStatus
  publishedAt?: string
  viewCount?: number
  shareCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface VideoRequest {
  title: string
  slug?: string
  description?: string
  category: VideoCategory
  youtubeId?: string | null
  externalUrl?: string | null
  thumbnailUrl?: string | null
  duration?: string | null
  language?: string | null
  sourceOrg?: string | null
  isFeatured?: boolean
  status?: ResourceStatus
}

export interface VideoStats {
  TOTAL: number
  DRAFT: number
  PUBLISHED: number
  ARCHIVED: number
}

export interface VideoFacetBucket { key: string; count: number }
export interface VideoFacets { categories: VideoFacetBucket[] }

// ---- public ----

export async function listVideos(params: {
  category?: string
  language?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Video>> {
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.language) qs.set('language', params.language)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 12))
  const res = await api.get<ApiResponse<PaginatedResponse<Video>>>(`/videos?${qs.toString()}`)
  return res.data.data
}

export async function getVideoFacets(): Promise<VideoFacets> {
  const res = await api.get<ApiResponse<VideoFacets>>('/videos/facets')
  return res.data.data
}

export async function getVideo(slug: string): Promise<Video> {
  const res = await api.get<ApiResponse<Video>>(`/videos/${slug}`)
  return res.data.data
}

/** Public: record a social share of a video (increments the share counter). No auth. */
export async function shareVideo(id: number): Promise<number> {
  const res = await api.post<ApiResponse<{ shareCount: number }>>(`/videos/${id}/share`)
  return res.data.data.shareCount
}

// ---- admin ----

export async function listVideosAdmin(params: {
  status?: string
  category?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Video>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.category) qs.set('category', params.category)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<Video>>>(`/videos/admin?${qs.toString()}`)
  return res.data.data
}

export async function getVideoStats(): Promise<VideoStats> {
  const res = await api.get<ApiResponse<VideoStats>>('/videos/stats')
  return res.data.data
}

export async function createVideo(req: VideoRequest): Promise<Video> {
  const res = await api.post<ApiResponse<Video>>('/videos', req)
  return res.data.data
}

export async function updateVideo(id: number, req: VideoRequest): Promise<Video> {
  const res = await api.put<ApiResponse<Video>>(`/videos/${id}`, req)
  return res.data.data
}

export async function changeVideoStatus(id: number, status: ResourceStatus): Promise<Video> {
  const res = await api.put<ApiResponse<Video>>(`/videos/${id}/status?status=${status}`)
  return res.data.data
}

export async function deleteVideo(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/videos/${id}`)
}

// ---- label maps ----

export const VIDEO_CATEGORY_KEYS: VideoCategory[] = [
  'TB_AWARENESS', 'SYMPTOMS', 'TREATMENT', 'NUTRITION', 'STIGMA',
  'SURVIVOR_STORIES', 'NIKSHAY_MITRA', 'COMMUNITY_CAMPAIGNS',
]

export const VIDEO_CATEGORY_LABELS: Record<VideoCategory, string> = {
  TB_AWARENESS: 'TB Awareness',
  SYMPTOMS: 'Symptoms',
  TREATMENT: 'Treatment',
  NUTRITION: 'Nutrition',
  STIGMA: 'Stigma',
  SURVIVOR_STORIES: 'Survivor Stories',
  NIKSHAY_MITRA: 'Ni-kshay Mitra',
  COMMUNITY_CAMPAIGNS: 'Community Campaigns',
}

export const VIDEO_LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  hi: 'हिन्दी',
  mr: 'मराठी',
  multi: 'Multilingual',
}

export const VIDEO_STATUS_LABELS: Record<ResourceStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
}

/**
 * Turn an official video record into the right embed URL for an <iframe>.
 * YouTube → youtube-nocookie embed; Google Drive file link → preview endpoint;
 * anything else → null (the card falls back to an "Open official video" link).
 */
export function videoEmbedUrl(v: Video): string | null {
  if (v.youtubeId) {
    return `https://www.youtube-nocookie.com/embed/${v.youtubeId}?rel=0&modestbranding=1`
  }
  if (v.externalUrl) {
    const drive = v.externalUrl.match(/drive\.google\.com\/file\/d\/([^/]+)/)
    if (drive) return `https://drive.google.com/file/d/${drive[1]}/preview`
  }
  return null
}